(function () {
  // Sets up a global object to hold custom functions and classes.
  window.mx = window.mx || {};

  /**
   * Creates a throttled function that only invokes the provided callback function at most once per every animation frame.
   *
   * @param {Function} callback - The callback function to be throttled.
   * @returns {Function} - The throttled function.
   */
  window.mx.throttle = (callback) => {
    let requestId = null;
    let lastArgs;

    const later = (context) => () => {
      requestId = null;
      callback.apply(context, lastArgs);
    };

    const throttled = (...args) => {
      lastArgs = args;
      if (requestId === null) {
        requestId = requestAnimationFrame(later(this));
      }
    };

    throttled.cancel = () => {
      cancelAnimationFrame(requestId);
      requestId = null;
    };

    return throttled;
  };

  /**
   * Creates a debounced function that delays invoking the provided callback function until after a specified delay.
   *
   * @param {Function} callback - The callback function to be debounced.
   * @param {number} [delay=100] - The delay in milliseconds before invoking the callback function.
   * @returns {Function} - The debounced function.
   */
  window.mx.debounce = (callback, delay = 100) => {
    let timer = null;

    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        callback.apply(this, args);
      }, delay);
    };
  };

  /**
   * Calculates the width of the scrollbar and sets it as a CSS custom property.
   * This function is useful for maintaining layout consistency when scrollbars appear or disappear.
   *
   * The calculated width is set as the value of the '--scrollbar-width' CSS custom property.
   * This property can then be used in CSS to adjust layouts based on the presence or absence of a scrollbar.
   */
  window.mx.setScrollBarWidthProperty = () => {
    const scrollBarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty(
      "--scrollbar-width",
      `${scrollBarWidth}px`,
    );
  };
  window.addEventListener(
    "resize",
    window.mx.throttle(() => window.mx.setScrollBarWidthProperty()),
  );
  window.mx.setScrollBarWidthProperty();

  /**
   * Locks scrolling on the provided element and saves the current scroll position.
   *
   * @param {HTMLElement} - The element to lock scrolling on. Defaults to HTML element.
   */
  window.mx.lockScroll = (element = document.documentElement) => {
    element.style.scrollBehavior = "auto";
    element.style.top = `-${window.scrollY}px`;
    element.setAttribute("data-scroll-lock", "");
  };

  /**
   * Unlocks scrolling on the provided element and restores the previous scroll position.
   *
   * @param {HTMLElement} - The element to unlock scrolling on. Defaults to HTML element.
   */
  window.mx.unlockScroll = (element = document.documentElement) => {
    element.removeAttribute("data-scroll-lock");
    window.scrollTo(0, -1 * parseInt(element.style.top));
    element.style.top = null;
    element.style.scrollBehavior = null;
  };

  window.mx.MxSection = class extends HTMLElement {
    constructor() {
      super();
    }

    get section() {
      return this.closest(".mx-section");
    }
    get menuDrawer() {
      return document.querySelector(".mx-menu-drawer");
    }
    get searchDrawer() {
      return document.querySelector(".mx-search-drawer");
    }
    get accountDrawer() {
      return document.querySelector(".mx-account-drawer");
    }
    get cartDrawer() {
      return document.querySelector(".mx-cart-drawer");
    }
  };
  if (!window.customElements.get("mx-section")) {
    window.customElements.define("mx-section", window.mx.MxSection);
  }

  window.mx.MxDrawer = class extends window.mx.MxSection {
    constructor() {
      super();
    }

    get overlay() {
      return this.parentElement.querySelector(".mx-overlay");
    }
  };
  if (!window.customElements.get("mx-drawer")) {
    window.customElements.define("mx-drawer", window.mx.MxDrawer);
  }

  window.mx.MxSlider = class extends HTMLUListElement {
    cachedConfig = null;
    eventHandlerList = [];
    pendingScroll = false;
    activeIndex = 0;
    isDragging = false;
    dragPosition = {};
    nearingStart = false;
    leavingStart = true;
    nearingEnd = true;
    leavingEnd = false;

    constructor() {
      super();

      if (this.slideList.length === 0) return;

      this.addEventListener(
        "scroll",
        window.mx.throttle(() => this.onScroll()),
      );
      this.addEventListener("scrollend", () => this.onScrollEnd());

      new ResizeObserver(this.onResize).observe(this);

      if (this.allowDrag) {
        const mediaQuery = window.matchMedia("screen and (pointer: fine)");

        mediaQuery.addEventListener("change", (event) => {
          if (event.matches) {
            this.addEventListener("pointerdown", this.onPointerDown);
          } else {
            this.removeEventListener("pointerdown", this.onPointerDown);
          }
        });

        if (mediaQuery.matches) {
          this.addEventListener("pointerdown", this.onPointerDown);
        }
      } else {
        this.style.setProperty("cursor", "auto");
      }
    }

    connectedCallback() {
      this.activeIndex = Math.max(
        0,
        this.slideList.findIndex((slide) => slide.hasAttribute("data-initial")),
      );

      if (this.activeIndex > 0) {
        this.select(this.activeIndex, "auto");
      }

      if (this.adaptiveHeight) {
        this.adaptHeight();
      }
    }

    disconnectedCallback() {
      this.eventHandlerList.forEach(({ element, type, handler }) => {
        element.removeEventListener(type, handler);
      });
      this.eventHandlerList = [];
    }

    get config() {
      if (this.cachedConfig === null) {
        const configElement = document.querySelector(`#${this.id}__config`);

        try {
          this.cachedConfig = configElement
            ? JSON.parse(configElement.textContent)
            : {};
        } catch (error) {
          console.error("Error parsing slider JSON configuration", this, error);
        }
      }

      return this.cachedConfig;
    }
    get slideList() {
      return Array.from(this.children);
    }
    get activeSlide() {
      return this.slideList[this.activeIndex];
    }
    get fullyVisibleSlideCount() {
      return [...this.slideList]
        .map((slide) => {
          const slideLeft = slide.offsetLeft;
          const slideRight = slideLeft + slide.clientWidth;

          return (
            slideRight <= this.scrollLeft + this.clientWidth &&
            slideLeft >= this.scrollLeft
          );
        })
        .filter((visibleSlide) => visibleSlide).length;
    }
    get allowDrag() {
      return this.config?.allowDrag ?? true;
    }
    get loopOver() {
      return this.config?.loopOver ?? false;
    }
    get adaptiveHeight() {
      return this.config?.adaptiveHeight ?? false;
    }

    previous(behavior) {
      if (this.loopOver && this.leavingStart) {
        this.select(
          this.slideList.length - this.fullyVisibleSlideCount,
          behavior,
        );
      } else {
        this.select(Math.max(this.activeIndex - 1, 0), behavior);
      }
    }
    next(behavior) {
      if (this.loopOver && this.leavingEnd) {
        this.select(0, behavior);
      } else {
        this.select(
          Math.min(this.activeIndex + 1, this.slideList.length - 1),
          behavior,
        );
      }
    }
    select(index, behavior) {
      if (
        !(index in this.slideList) ||
        !this.checkVisibility() ||
        this.offsetWidth === 0 ||
        this.offsetHeight === 0
      )
        return;

      this.dispatchEvent(
        new CustomEvent("mx-slider:select", {
          detail: { index, slide: this.slideList[index] },
        }),
      );

      this.updateActiveIndex(index);
      this.pendingScroll = true;
      this.scrollTo({
        left: this.calculateLeftScroll(this.slideList[index]),
        behavior: behavior,
      });
    }
    updateActiveIndex(index) {
      if (index === this.activeIndex) return;

      this.activeIndex = index;

      this.dispatchEvent(
        new CustomEvent("mx-slider:change", {
          detail: { index: this.activeIndex, slide: this.activeSlide },
        }),
      );
    }
    calculateLeftScroll(slide) {
      const scrollPadding = getComputedStyle(this).scrollPaddingInlineStart;
      const scrollPaddingValue =
        scrollPadding === "auto" ? 0 : parseInt(scrollPadding);
      const scrollLeft = slide.offsetLeft - scrollPaddingValue;

      return Math.min(
        Math.max(scrollLeft, 0),
        this.scrollWidth - this.clientWidth,
      );
    }
    calculateClosestIndex() {
      const cellOffsetList = this.slideList.map((slide) => slide.offsetLeft);

      return cellOffsetList.indexOf(
        cellOffsetList.reduce((prev, curr) =>
          Math.abs(curr - this.scrollLeft) < Math.abs(prev - this.scrollLeft)
            ? curr
            : prev,
        ),
      );
    }
    adaptHeight() {
      if (this.clientHeight === this.activeSlide.clientHeight) return;

      this.style.removeProperty("max-height");
      if (this.scrollHeight != this.clientHeight) {
        this.style.maxHeight = `${this.activeSlide.clientHeight}px`;
      }
    }
    onScroll() {
      const scrollEdgeThreshold = 100;

      if (this.scrollLeft < scrollEdgeThreshold && this.nearingStart) {
        this.dispatchEvent(
          new CustomEvent("mx-slider:edge-nearing", { detail: "start" }),
        );
        this.nearingStart = false;
        this.leavingStart = true;
      }

      if (this.scrollLeft >= scrollEdgeThreshold && this.leavingStart) {
        this.dispatchEvent(
          new CustomEvent("mx-slider:edge-leaving", { detail: "start" }),
        );
        this.nearingStart = true;
        this.leavingStart = false;
      }

      if (
        this.scrollWidth - this.clientWidth <
          this.scrollLeft + scrollEdgeThreshold &&
        this.nearingEnd
      ) {
        this.dispatchEvent(
          new CustomEvent("mx-slider:edge-nearing", { detail: "end" }),
        );
        this.nearingEnd = false;
        this.leavingEnd = true;
      }

      if (
        this.scrollWidth - this.clientWidth >=
          this.scrollLeft + scrollEdgeThreshold &&
        this.leavingEnd
      ) {
        this.dispatchEvent(
          new CustomEvent("mx-slider:edge-leaving", { detail: "end" }),
        );
        this.leavingEnd = false;
        this.nearingEnd = true;
      }
    }
    onScrollEnd() {
      this.pendingScroll = false;

      if (
        this.isDragging ||
        this.activeIndex < this.slideList.length - this.fullyVisibleSlideCount
      ) {
        this.updateActiveIndex(this.calculateClosestIndex());
      }

      this.dispatchEvent(
        new CustomEvent("mx-slider:settle", {
          detail: { index: this.activeIndex, slide: this.activeSlide },
        }),
      );
    }
    onPointerDown(event) {
      this.dragPosition = {
        left: this.scrollLeft,
        top: this.scrollTop,
        x: event.clientX,
        y: event.clientY,
      };

      this.isDragging = true;

      document.addEventListener("pointermove", this.onPointerMove);
      document.addEventListener("pointerup", this.onPointerUp);
      this.addEventListener("click", this.onClick, { once: true });
    }
    onPointerMove = (event) => {
      if (!this.isDragging) return;

      event.preventDefault();

      const [dx, dy] = [
        event.clientX - this.dragPosition.x,
        event.clientY - this.dragPosition.y,
      ];

      this.scrollTop = this.dragPosition.top - dy;
      this.scrollLeft = this.dragPosition.left - dx;
    };
    onClick(event) {
      if (event.clientX - this.dragPosition.x !== 0) {
        event.preventDefault();
      }
    }
    onPointerUp = () => {
      this.isDragging = false;

      if (!this.pendingScroll) {
        this.scrollTo({
          left: this.calculateLeftScroll(this.activeSlide),
          behavior: "smooth",
        });
      }

      document.removeEventListener("pointermove", this.onPointerMove);
      document.removeEventListener("pointerup", this.onPointerUp);
    };
    onResize() {
      if (this.activeIndex !== this.calculateClosestIndex) {
        this.select(this.activeIndex, "smooth");
      }

      if (this.adaptiveHeight) {
        this.adaptHeight();
      }
    }
  };
  if (!window.customElements.get("mx-slider")) {
    window.customElements.define("mx-slider", window.mx.MxSlider, {
      extends: "ul",
    });
  }

  window.mx.MxSliderNavigation = class extends HTMLUListElement {
    cachedSlider = null;
    eventHandlerList = [];

    constructor() {
      super();
    }

    connectedCallback() {
      if (!this.slider) {
        throw new Error(
          "Slider navigation component requires an aria-controls attribute that refers to the controlled slider.",
        );
      }

      this.navigationItemList.forEach((navigationItem, index) => {
        navigationItem.addEventListener("click", () => this.onClick(index));
      });

      this.slider.addEventListener("mx-slider:change", (event) =>
        this.onNavigationChange(event.detail.index),
      );
    }

    disconnectedCallback() {
      this.eventHandlerList.forEach(({ element, type, handler }) => {
        element.removeEventListener(type, handler);
      });
      this.eventHandlerList = [];
    }

    get slider() {
      if (this.cachedSlider === null) {
        const sliderElement = document.getElementById(
          this.getAttribute("aria-controls"),
        );
        if (!sliderElement) return null;

        this.cachedSlider = sliderElement;
      }

      return this.cachedSlider;
    }
    get navigationItemList() {
      return Array.from(this.children);
    }

    onClick(index) {
      this.slider.select(index, "smooth");
      this.onNavigationChange(index);
    }
    onNavigationChange(newIndex) {
      this.navigationItemList.forEach((navigationItem, index) => {
        navigationItem.setAttribute(
          "aria-current",
          index === newIndex ? "true" : "false",
        );
      });

      if (this.scrollWidth !== this.clientWidth) {
        this.scrollTo({
          left:
            this.navigationItemList[newIndex].offsetLeft -
            this.clientWidth / 2 +
            this.navigationItemList[newIndex].clientWidth / 2,
          behavior: "smooth",
        });
      } else if (this.scrollHeight !== this.clientHeight) {
        this.scrollTo({
          top:
            this.navigationItemList[newIndex].offsetTop -
            this.clientHeight / 2 +
            this.navigationItemList[newIndex].clientHeight / 2,
          behavior: "smooth",
        });
      }
    }
  };
  if (!window.customElements.get("mx-slider-navigation")) {
    window.customElements.define(
      "mx-slider-navigation",
      window.mx.MxSliderNavigation,
      {
        extends: "ul",
      },
    );
  }

  window.mx.MxSliderNavigationButton = class extends HTMLButtonElement {
    cachedSlider = null;
    eventHandlerList = [];

    constructor() {
      super();
    }

    connectedCallback() {
      if (!this.slider) {
        throw new Error(
          "Slider navigation button component requires an aria-controls attribute that refers to the controlled slider.",
        );
      } else if (!this.direction) {
        throw new Error(
          "Slider navigation button component requires either a data-next or data-previous attribute.",
        );
      }

      this.addEventListener("click", () =>
        this.direction === "next"
          ? this.slider.next("smooth")
          : this.slider.previous("smooth"),
      );

      if (!this.loopOver) {
        let edgeNearingHandler;
        let edgeLeavingHandler;

        if (this.direction === "next") {
          edgeNearingHandler = (event) =>
            (this.disabled = event.detail === "end");
          edgeLeavingHandler = (event) =>
            (this.disabled = event.detail === "end" ? false : this.disabled);
          if (
            this.slider.activeIndex >=
            this.slider.slideList.length - this.slider.fullyVisibleSlideCount
          )
            this.disabled = true;
        } else {
          edgeNearingHandler = (event) =>
            (this.disabled = event.detail === "start");
          edgeLeavingHandler = (event) =>
            (this.disabled = event.detail === "start" ? false : this.disabled);
          if (this.slider.activeIndex === 0) this.disabled = true;
        }

        this.slider.addEventListener(
          "mx-slider:edge-nearing",
          edgeNearingHandler,
        );

        this.slider.addEventListener(
          "mx-slider:edge-leaving",
          edgeLeavingHandler,
        );
      }
    }

    disconnectedCallback() {
      this.eventHandlerList.forEach(({ element, type, handler }) => {
        element.removeEventListener(type, handler);
      });
      this.eventHandlerList = [];
    }

    get slider() {
      if (this.cachedSlider === null) {
        const sliderElement = document.getElementById(
          this.getAttribute("aria-controls"),
        );
        if (!sliderElement) return null;

        this.cachedSlider = sliderElement;
      }

      return this.cachedSlider;
    }
    get direction() {
      if (
        !this.hasAttribute("data-next") &&
        !this.hasAttribute("data-previous")
      )
        return null;

      return this.hasAttribute("data-next") ? "next" : "previous";
    }
    get loopOver() {
      return this.slider?.config?.loopOver ?? false;
    }
  };
  if (!window.customElements.get("mx-slider-navigation-button")) {
    window.customElements.define(
      "mx-slider-navigation-button",
      window.mx.MxSliderNavigationButton,
      {
        extends: "button",
      },
    );
  }
})();
